import { useState, useEffect, useRef } from 'react'
import { Send, X } from 'lucide-react'
import type { Reflection, Participant } from '../../types'

interface ReflectionPanelProps {
  reflection: Reflection
  participants: Participant[]
  currentParticipantId: string
  sessionId: string
  onSubmit: (content: string) => void
  isCreator: boolean
  onForceComplete: () => void
  onMinimize: () => void
}

export default function ReflectionPanel({
  reflection,
  participants,
  currentParticipantId,
  onSubmit,
  isCreator,
  onForceComplete,
  onMinimize,
}: ReflectionPanelProps) {
  const [text, setText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [autoCloseSeconds, setAutoCloseSeconds] = useState(90)
  const [timesUp, setTimesUp] = useState(false)
  const autoCloseRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const reflectionIdRef = useRef(reflection.id)

  const hasSubmitted = !!reflection.responses[currentParticipantId]
  const submittedCount = Object.keys(reflection.responses).length
  const totalCount = participants.length

  // Auto-close countdown timer
  useEffect(() => {
    // Reset timer if reflection changes
    if (reflection.id !== reflectionIdRef.current) {
      reflectionIdRef.current = reflection.id
      setAutoCloseSeconds(90)
      setTimesUp(false)
    }

    autoCloseRef.current = setInterval(() => {
      setAutoCloseSeconds(prev => {
        if (prev <= 1) {
          if (autoCloseRef.current) clearInterval(autoCloseRef.current)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => {
      if (autoCloseRef.current) clearInterval(autoCloseRef.current)
    }
  }, [reflection.id])

  // When timer expires, auto-close if user has submitted; otherwise show "time's up"
  useEffect(() => {
    if (autoCloseSeconds > 0) return
    if (hasSubmitted) {
      onForceComplete()
    } else {
      setTimesUp(true)
    }
  }, [autoCloseSeconds, hasSubmitted, onForceComplete])

  async function handleSubmit() {
    const trimmed = text.trim()
    if (!trimmed || submitting) return
    setSubmitting(true)
    try {
      onSubmit(trimmed)
      setText('')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
      {/* Semi-transparent backdrop, clickable to minimize so the chat stays readable */}
      <div
        className="absolute inset-0 bg-navy/25 pointer-events-auto animate-fade-in"
        onClick={onMinimize}
      />

      {/* Modal card */}
      <div className="relative card card-shadow w-full max-w-lg mx-4 pointer-events-auto max-h-[70vh] overflow-y-auto animate-modal-enter">
        <div className="p-5 sm:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="hl text-xl text-navy">Reflection round</h3>
            <div className="flex items-center gap-2">
              {autoCloseSeconds > 0 && (
                <span className="mono-label text-navy/70 tabular-nums">
                  Closes in {autoCloseSeconds}s
                </span>
              )}
              <span className="mono-label text-navy/70 bg-paper border border-navy/40 px-2 py-1">
                {submittedCount}/{totalCount} in
              </span>
              <button
                onClick={onMinimize}
                className="p-1 hover:bg-paper transition-colors cursor-pointer text-navy/70 hover:text-navy"
                title="Minimize to read chat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Prompt: render the full question text with line breaks preserved */}
          <div className="text-base text-navy/85 mb-4 leading-relaxed bg-paper border-l-2 border-amber p-3 whitespace-pre-wrap">
            {reflection.prompt}
          </div>

          {/* Submission dots */}
          <div className="flex gap-2 mb-4">
            {participants.map((p) => {
              const submitted = !!reflection.responses[p.id]
              return (
                <div key={p.id} className="flex flex-col items-center gap-1">
                  <div
                    className={`w-6 h-6 rounded-full border-2 border-navy flex items-center justify-center text-[10px] font-semibold text-white transition-opacity ${
                      submitted ? 'opacity-100' : 'opacity-35'
                    }`}
                    style={{ backgroundColor: p.color }}
                    title={`${p.displayName}${submitted ? ' (submitted)' : ''}`}
                  >
                    {p.displayName.charAt(0).toUpperCase()}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Time's up nudge */}
          {timesUp && !hasSubmitted && (
            <div className="mb-3 text-center">
              <p className="mono-label text-brick">
                Time is up. Send what you have.
              </p>
            </div>
          )}

          {/* Input or submitted state */}
          {hasSubmitted ? (
            <div>
              {reflection.allSubmitted ? (
                <div className="space-y-3">
                  <p className="mono-label text-teal mb-2">All responses</p>
                  {participants.map((p) => {
                    const response = reflection.responses[p.id]
                    if (!response) return null
                    return (
                      <div key={p.id} className="bg-paper border border-navy/25 p-3">
                        <p className="mono-label mb-1" style={{ color: p.color }}>
                          {p.displayName}
                        </p>
                        <p className="text-base text-navy/85 whitespace-pre-wrap">
                          {response.content}
                        </p>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="mono-label text-navy/72" aria-live="polite">
                    Sent. Waiting for the others
                    <span className="caret" aria-hidden="true" />
                  </p>
                  {isCreator && submittedCount >= 1 && (
                    <button
                      onClick={onForceComplete}
                      className="btn mt-4"
                    >
                      Close round ({submittedCount}/{totalCount} in)
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-end gap-2">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Write your answer"
                rows={3}
                className="flex-1 resize-none bg-paper border-2 border-navy/55 focus:border-navy px-3.5 py-2.5 text-base outline-none placeholder:text-navy/70"
              />
              <button
                onClick={handleSubmit}
                disabled={!text.trim() || submitting}
                className="p-2.5 bg-navy text-stone border-2 border-navy disabled:opacity-30 hover:bg-navy-lifted transition-colors cursor-pointer disabled:cursor-default"
                aria-label="Send your reflection"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
